import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { Star, Pencil, Trash2, MoreHorizontal, MessageSquare, Link } from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import { useVaultStore } from '../../store/vaultStore';
import { VAULT_TYPE_META, omitPasskeyKeys } from '../VaultItemModal';
import VaultShareModal from '../VaultShareModal';
import VaultSecretLinkModal from '../VaultSecretLinkModal';
import VaultDetailClose from '../VaultDetailClose';
import VaultSiteIcon from '../VaultSiteIcon';
import { buildFieldRows } from './vaultItemFields';
import { FieldRow } from './VaultFieldRow';
import { ItemMenu } from './VaultItemMenu';

export function VaultItemDetail({ envelope, onEdit, onClose, siteIconBase = null, siteKey = null }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [secretLinkOpen, setSecretLinkOpen] = useState(false);
  const menuBtnRef = useRef(null);

  // Decrypt on selection (and after each edit — rev bumps on every put).
  useEffect(() => {
    let alive = true;
    setData(null);
    setLoadError(null);
    setConfirmDelete(false);
    setMenuOpen(false);
    setShareOpen(false);
    setSecretLinkOpen(false);
    invoke('vault_get_item', { id: envelope.id })
      .then((full) => {
        if (alive) setData(full?.data || {});
      })
      .catch((e) => {
        if (alive) setLoadError(String(e?.message || e));
      });
    return () => {
      alive = false;
    };
  }, [envelope.id, envelope.rev]);

  const meta = VAULT_TYPE_META[envelope.type] || VAULT_TYPE_META.custom;
  const TypeIcon = meta.Icon;
  const rows = data ? buildFieldRows(envelope.type, data, t) : [];
  // Plaintext index only — `data.passkeys` is stripped by vault_get_item and
  // is never read here (VaultPasskeyList / passkey_list_for_item owns that).
  const passkeyCount = Number(envelope.index?.passkeyCount) || 0;

  // Stable decrypted-item reference for the share/secret-link modals. Without
  // this, VaultItemDetail re-renders (e.g. from a vault:changed refresh mid
  // share) would hand the modal a brand-new object each time and wipe its
  // in-flight state — so key it on the identity that actually changed.
  // Passkey state is dropped here: `data` came from vault_get_item, so its
  // records have no key material, and a share carrying those husks would give
  // the recipient credentials that cannot sign anything.
  const shareItem = useMemo(
    () => (data ? { ...envelope, data: omitPasskeyKeys(data) } : null),
    // Intentionally keyed on id+rev (not the whole envelope) — that instability
    // is exactly what we're insulating the modals from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [envelope.id, envelope.rev, data]
  );

  const startEdit = () => {
    if (!data) return;
    onEdit({ ...envelope, data });
  };

  const toggleFavorite = async () => {
    if (!data || busy) return;
    setBusy(true);
    try {
      await useVaultStore.getState().putItem({
        id: envelope.id,
        type: envelope.type,
        name: envelope.name,
        sync: envelope.sync,
        favorite: !envelope.favorite,
        // `data` came from vault_get_item, so its passkey records are stripped
        // copies with no key material — writing them back would replace the real
        // ones with husks. Omit the keys instead: absent is what will mean "keep
        // what is stored" once vault_put_item re-attaches them.
        data: omitPasskeyKeys(data),
      });
    } catch (e) {
      console.warn('[vault] favorite toggle failed:', e?.message || e);
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await useVaultStore.getState().deleteItem(envelope.id);
    } catch (e) {
      console.warn('[vault] delete failed:', e?.message || e);
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  /* vault-item-menu-extensions */
  // Later agents append entries here (e.g. "Share in chat" / "Secret link")
  // — keep this a plain array of { id, label, Icon, danger?, onSelect }.
  const menuItems = [
    { id: 'edit', label: t('common.edit'), Icon: Pencil, onSelect: startEdit },
    {
      id: 'favorite',
      label: envelope.favorite ? t('vault.unfavorite') : t('vault.favorite'),
      Icon: Star,
      onSelect: toggleFavorite,
    },
    {
      id: 'share-chat',
      label: t('vaultShare.menuShareInChat', 'Share in chat…'),
      Icon: MessageSquare,
      onSelect: () => {
        if (data) setShareOpen(true);
      },
    },
    {
      id: 'secret-link',
      label: t('vaultShare.menuSecretLink', 'Create secret link…'),
      Icon: Link,
      onSelect: () => {
        if (data) setSecretLinkOpen(true);
      },
    },
    {
      id: 'delete',
      label: t('common.delete'),
      Icon: Trash2,
      danger: true,
      onSelect: () => setConfirmDelete(true),
    },
  ];

  return (
    <div className="vault-detail">
      <div className="vault-detail-header">
        <div className="vault-detail-icon">
          {/* The row's own icon (website icon or type icon), larger. */}
          <VaultSiteIcon base={siteIconBase} siteKey={siteKey} Fallback={TypeIcon} size={16} px={20} />
        </div>
        <div className="vault-detail-titles">
          <div className="vault-detail-title">{envelope.name}</div>
          <div className="vault-detail-sub">
            {t(meta.labelKey)}
            {envelope.updatedAt
              ? ` · ${t('vault.updated')} ${new Date(envelope.updatedAt).toLocaleDateString()}`
              : ''}
          </div>
        </div>
        <div className="vault-detail-actions">
          {confirmDelete ? (
            <>
              {/* Deleting the item destroys its private keys — there is no
                  export in v1 and no way back — so the count is named. */}
              <span className="vault-confirm-label">
                {passkeyCount > 0
                  ? passkeyCount === 1
                    ? t('vault.deleteConfirmPasskeyOne', 'Delete? 1 passkey is destroyed.')
                    : t(
                        'vault.deleteConfirmPasskeyOther',
                        'Delete? {{count}} passkeys are destroyed.',
                        { count: passkeyCount }
                      )
                  : t('vault.deleteConfirm')}
              </span>
              <button
                type="button"
                className="vault-action-btn vault-action-btn--danger"
                disabled={busy}
                onClick={doDelete}
              >
                {t('common.delete')}
              </button>
              <button
                type="button"
                className="vault-action-btn"
                disabled={busy}
                onClick={() => setConfirmDelete(false)}
              >
                {t('common.cancel')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={`vault-action-btn${envelope.favorite ? ' vault-action-btn--starred' : ''}`}
                title={envelope.favorite ? t('vault.unfavorite') : t('vault.favorite')}
                disabled={!data || busy}
                onClick={toggleFavorite}
              >
                <Star size={14} fill={envelope.favorite ? 'currentColor' : 'none'} />
              </button>
              <button
                type="button"
                className="vault-action-btn"
                title={t('common.edit')}
                disabled={!data}
                onClick={startEdit}
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                className="vault-action-btn vault-action-btn--danger"
                title={t('common.delete')}
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 size={14} />
              </button>
              <button
                ref={menuBtnRef}
                type="button"
                className="vault-action-btn"
                title={t('vault.moreActions')}
                onClick={() => setMenuOpen((o) => !o)}
              >
                <MoreHorizontal size={14} />
              </button>
            </>
          )}
        </div>
        {onClose && <VaultDetailClose onClose={onClose} />}
      </div>

      <OsScroll style={{ flex: 1, minHeight: 0 }}>
        {loadError ? (
          <div className="vault-empty">
            <span className="vault-empty-sub">{t('vault.loadFailed')}</span>
          </div>
        ) : (
          <div className="vault-fields">
            {rows.map((row) => (
              <FieldRow key={`${envelope.id}:${row.id}`} row={row} t={t} />
            ))}
          </div>
        )}
      </OsScroll>

      {menuOpen && (
        <ItemMenu anchorRef={menuBtnRef} items={menuItems} onClose={() => setMenuOpen(false)} />
      )}

      {/* Both take the DECRYPTED item ({ ...envelope, data }) — same payload
          the detail pane already fetched via vault_get_item. Passed as a
          memoized ref so a re-render can't reset the modal mid-flow. */}
      <VaultShareModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        item={shareItem}
      />
      <VaultSecretLinkModal
        isOpen={secretLinkOpen}
        onClose={() => setSecretLinkOpen(false)}
        item={shareItem}
      />
    </div>
  );
}
